import { ReactNode, useCallback } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useModalControl } from '@frontend/design-system';
import { FeatureDemoModal } from '../components/feature-demo-modal/FeatureDemoModal';
import { FeatureSubscriptionContext } from '../providers/FeatureSubscriptionProvider';
import { useFeatureSubscriptionConfig } from './useFeatureSubscriptionConfig';
import { useFeatureUpgradeDetailsModal } from './useFeatureUpgradeDetailsModal';

const STANDARD_ACTIVE_STATES = [State.ACTIVE];
const EXTENDED_ACTIVE_STATES = [State.ACTIVE, State.PROMOTE];

type UseFeatureDemoModalParams = {
  feature: Feature;
  locationIds?: string[];
  demoModalContent?: ReactNode;
  onDemoModalOpen?: () => void;
  onDemoModalClose?: () => void;
} & (
  | {
      allowPromoteState?: boolean;
      activeStates?: never;
    }
  | {
      allowPromoteState?: never;
      activeStates?: State[];
    }
);

export const useFeatureDemoModal = ({
  feature,
  locationIds,
  demoModalContent,
  allowPromoteState,
  activeStates = STANDARD_ACTIVE_STATES,
  onDemoModalOpen,
  onDemoModalClose,
}: UseFeatureDemoModalParams) => {
  const { modalProps, openModal, closeModal } = useModalControl();

  const featureSubscriptionConfig = useFeatureSubscriptionConfig({
    feature,
    locationIds,
    activeStates: allowPromoteState ? EXTENDED_ACTIVE_STATES : activeStates,
  });

  const { isBundleUpgradeEligible } = featureSubscriptionConfig;

  const { FeatureUpgradeDetailsModal, featureUpgradeDetailsModalControls } = useFeatureUpgradeDetailsModal({
    feature,
    isBundleUpgradeEligible,
  });

  const openDemoModal = useCallback(() => {
    openModal();
    onDemoModalOpen?.();
  }, [openModal, onDemoModalOpen]);

  const closeDemoModal = useCallback(() => {
    onDemoModalClose?.();
    closeModal();
  }, [closeModal, onDemoModalClose]);

  const featureDemoModal = demoModalContent ? (
    <FeatureSubscriptionContext.Provider value={featureSubscriptionConfig}>
      {!!FeatureUpgradeDetailsModal && (
        <FeatureUpgradeDetailsModal
          modalControls={featureUpgradeDetailsModalControls}
          onActionSuccess={featureUpgradeDetailsModalControls.closeModal}
        />
      )}

      <FeatureDemoModal
        onUpgradeAction={featureUpgradeDetailsModalControls.openModal}
        {...modalProps}
        onClose={closeDemoModal}
      >
        {modalProps.show && demoModalContent}
      </FeatureDemoModal>
    </FeatureSubscriptionContext.Provider>
  ) : null;

  return {
    openDemoModal,
    closeDemoModal,
    isDemoAvailable: !!demoModalContent,
    FeatureDemoModal: featureDemoModal,
  };
};
